import React, { useState, useMemo } from 'react';
import { Calculator, Download, FileText, Settings, Zap, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from '../hooks/use-toast';

const SYSTEM_TYPES = {
  'Wenner': 17.3,
  'Schlumberger': 19.2,
  'Dipole-Dipole': 19.1,
  'Pole-Dipole': 36.0,
  'Pole-Pole': 56.0,
  'Inverse Schlumberger': 19.2,
  'Wenner-Schlumberger': 18.2,
  'Gradient': 20.0,
  'Strong Gradient': 20.0,
  'Edge Gradient': 20.0
};

const CableConfigurator = () => {
  const [systemType, setSystemType] = useState('Wenner');
  const [numElectrodes, setNumElectrodes] = useState(112);
  const [spacing, setSpacing] = useState(7.25);
  const [depthMax, setDepthMax] = useState(182.00);
  const [scalingFactor, setScalingFactor] = useState(17.3);
  const [unit, setUnit] = useState('metric'); // 'metric' or 'imperial'
  const [activeTab, setActiveTab] = useState('metric'); // Track active results tab
  const [calcMode, setCalcMode] = useState('depth'); // 'depth', 'spacing', or 'electrodes'

  // Conversion constants
  const M_TO_FT = 3.28084;
  const FT_TO_M = 1 / M_TO_FT;

  // Pricing parameters (from Passive Cable Price Calculator)
  const CABLE_PRICE_PER_METER = 1.3 * 3 / 0.3048; // 28 conductor default: $12.80/m
  const CONNECTOR_PRICE = 286.0;
  const ELECTRODE_PRICE = 90.0; // SS default
  const NUM_CONNECTORS = 2; // Standard configuration
  const OVERHEAD_MULTIPLIER = 1.13;
  const PROFIT_MULTIPLIER = 1.3;

  // Update scaling factor when system type changes
  // In "Depth → Spacing" and "Electrodes" modes, also update depthMax
  // so the investigation depth changes with each array type's scaling characteristics
  React.useEffect(() => {
    const newScaling = SYSTEM_TYPES[systemType];

    if (calcMode === 'depth' && scalingFactor !== 0) {
      // Keep current spacing, recalculate depth for new array type
      const depthM = unit === 'metric' ? depthMax : depthMax * FT_TO_M;
      const currentSpacingM = ((depthM * (1 / scalingFactor)) / numElectrodes) * 100;
      const newDepthM = (currentSpacingM * numElectrodes * newScaling) / 100;
      setDepthMax(unit === 'metric' ? newDepthM : newDepthM * M_TO_FT);
    } else if (calcMode === 'electrodes' && scalingFactor !== 0) {
      // Keep current electrode count, recalculate depth for new array type
      const spacingM = unit === 'metric' ? spacing : spacing * FT_TO_M;
      const depthM = unit === 'metric' ? depthMax : depthMax * FT_TO_M;
      const currentElectrodes = Math.round((depthM * 100) / (spacingM * scalingFactor));
      const newDepthM = (spacingM * currentElectrodes * newScaling) / 100;
      setDepthMax(unit === 'metric' ? newDepthM : newDepthM * M_TO_FT);
    }

    setScalingFactor(newScaling);
  }, [systemType]); // eslint-disable-line

  // Convert depth when switching units
  const handleUnitChange = (newUnit) => {
    if (newUnit !== unit) {
      if (newUnit === 'imperial') {
        // Convert from meters to feet
        if (calcMode === 'depth') {
          setDepthMax(depthMax * M_TO_FT);
        } else if (calcMode === 'spacing') {
          setSpacing(spacing * M_TO_FT);
        } else {
          // electrodes mode - convert both
          setDepthMax(depthMax * M_TO_FT);
          setSpacing(spacing * M_TO_FT);
        }
      } else {
        // Convert from feet to meters
        if (calcMode === 'depth') {
          setDepthMax(depthMax * FT_TO_M);
        } else if (calcMode === 'spacing') {
          setSpacing(spacing * FT_TO_M);
        } else {
          // electrodes mode - convert both
          setDepthMax(depthMax * FT_TO_M);
          setSpacing(spacing * FT_TO_M);
        }
      }
      setUnit(newUnit);
      setActiveTab(newUnit); // Auto-switch results tab
    }
  };

  // Calculate all values
  const calculations = useMemo(() => {
    let depth_m, spacing_m, electrodes;

    if (calcMode === 'depth') {
      // Mode 1: Calculate spacing from depth (original)
      depth_m = unit === 'metric' ? depthMax : depthMax * FT_TO_M;
      electrodes = numElectrodes;
      // Spacing formula: ((depth * (1/scaling)) / numElectrodes) * 100
      spacing_m = ((depth_m * (1 / scalingFactor)) / electrodes) * 100;
    } else if (calcMode === 'spacing') {
      // Mode 2: Calculate depth from spacing (reverse)
      spacing_m = unit === 'metric' ? spacing : spacing * FT_TO_M;
      electrodes = numElectrodes;
      // Reverse formula: Depth = (Spacing × Electrodes × Scaling) / 100
      depth_m = (spacing_m * electrodes * scalingFactor) / 100;
    } else {
      // Mode 3: Calculate number of electrodes from depth and spacing
      depth_m = unit === 'metric' ? depthMax : depthMax * FT_TO_M;
      spacing_m = unit === 'metric' ? spacing : spacing * FT_TO_M;
      // Formula: Electrodes = (Depth × 100) / (Spacing × Scaling)
      electrodes = Math.round((depth_m * 100) / (spacing_m * scalingFactor));
    }
    
    // Total Length = Number of Electrodes × Spacing
    const totalLength_m = electrodes * spacing_m;
    
    // Resolution = Spacing / 2
    const resolution_m = spacing_m / 2;

    // Price Calculation (using passive cable formula)
    // Formula: ROUNDUP((TotalLength×CablePrice + Connectors×ConnectorPrice + Electrodes×ElectrodePrice) × 1.13, 0) × 1.3
    const cableCost = totalLength_m * CABLE_PRICE_PER_METER;
    const connectorCost = NUM_CONNECTORS * CONNECTOR_PRICE;
    const electrodeCost = electrodes * ELECTRODE_PRICE;
    const subtotal = cableCost + connectorCost + electrodeCost;
    const subtotalWithOverhead = subtotal * OVERHEAD_MULTIPLIER;
    const totalPrice = Math.ceil(subtotalWithOverhead) * PROFIT_MULTIPLIER;

    // Convert to feet
    const depth_ft = depth_m * M_TO_FT;
    const spacing_ft = spacing_m * M_TO_FT;
    const totalLength_ft = totalLength_m * M_TO_FT;
    const resolution_ft = resolution_m * M_TO_FT;

    return {
      metric: {
        depth: depth_m,
        spacing: spacing_m,
        totalLength: totalLength_m,
        resolution: resolution_m,
        electrodes: electrodes,
        totalPrice: totalPrice
      },
      imperial: {
        depth: depth_ft,
        spacing: spacing_ft,
        totalLength: totalLength_ft,
        resolution: resolution_ft,
        electrodes: electrodes,
        totalPrice: totalPrice
      }
    };
  }, [systemType, numElectrodes, depthMax, spacing, unit, scalingFactor, calcMode]);

  // Unit converter functions
  const [converterInput, setConverterInput] = useState('');
  const [converterResult, setConverterResult] = useState('');

  const convertMeterToFeet = (meters) => {
    const result = meters * M_TO_FT;
    setConverterResult(result.toFixed(2));
  };

  const convertFeetToMeter = (feet) => {
    const result = feet * FT_TO_M;
    setConverterResult(result.toFixed(2));
  };

  // Export configuration
  const exportConfig = (format) => {
    const config = {
      systemType,
      numElectrodes,
      depthMax,
      unit,
      scalingFactor,
      metric: calculations.metric,
      imperial: calculations.imperial,
      timestamp: new Date().toISOString()
    };

    if (format === 'json') {
      const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cable-config-${Date.now()}.json`;
      a.click();
      toast({ title: 'Success', description: 'Configuration exported as JSON' });
    } else if (format === 'csv') {
      const csvContent = [
        'Parameter,Metric,Imperial',
        `Array Type,${systemType},${systemType}`,
        `Number of Electrodes,${numElectrodes},${numElectrodes}`,
        `Spacing,${calculations.metric.spacing.toFixed(2)} m,${calculations.imperial.spacing.toFixed(2)} ft`,
        `Total Length,${calculations.metric.totalLength.toFixed(2)} m,${calculations.imperial.totalLength.toFixed(2)} ft`,
        `Depth Max,${calculations.metric.depth.toFixed(2)} m,${calculations.imperial.depth.toFixed(2)} ft`,
        `Scaling Factor,${scalingFactor}%,${scalingFactor}%`,
        `Resolution Approx.,${calculations.metric.resolution.toFixed(2)} m,${calculations.imperial.resolution.toFixed(2)} ft`
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cable-config-${Date.now()}.csv`;
      a.click();
      toast({ title: 'Success', description: 'Configuration exported as CSV' });
    } else if (format === 'pdf') {
      // Create HTML content for PDF
      const pdfContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Smart Cable Configuration Report</title>
  <style>
    @page { margin: 2cm; }
    body { 
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
      margin: 0; 
      padding: 20px;
      color: #2c3e50;
      line-height: 1.6;
    }
    .header { 
      text-align: center; 
      margin-bottom: 40px; 
      padding: 30px;
      background: linear-gradient(135deg, #1e3a8a 0%, #10b981 100%);
      border-radius: 10px;
      color: white;
    }
    .header h1 { 
      margin: 0 0 10px 0; 
      font-size: 32px;
      font-weight: 600;
      letter-spacing: -0.5px;
    }
    .header p { 
      margin: 5px 0;
      font-size: 14px;
      opacity: 0.95;
    }
    .header .date {
      margin-top: 15px;
      font-size: 12px;
      opacity: 0.85;
    }
    .section { 
      margin: 30px 0;
      page-break-inside: avoid;
    }
    .section h2 { 
      color: #1e3a8a; 
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 15px 0;
      padding-bottom: 10px;
      border-bottom: 3px solid #10b981;
    }
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 15px;
      margin: 20px 0;
    }
    .summary-card {
      background: #f8fafc;
      border: 2px solid #e2e8f0;
      border-radius: 8px;
      padding: 15px;
      text-align: center;
    }
    .summary-card .label {
      font-size: 12px;
      color: #64748b;
      margin-bottom: 5px;
      text-transform: uppercase;
      font-weight: 600;
    }
    .summary-card .value {
      font-size: 24px;
      color: #1e3a8a;
      font-weight: 700;
    }
    table { 
      width: 100%; 
      border-collapse: collapse; 
      margin: 15px 0;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      border-radius: 8px;
      overflow: hidden;
    }
    th, td { 
      padding: 14px 16px; 
      text-align: left;
    }
    th { 
      background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
      color: white; 
      font-weight: 600;
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    td {
      border-bottom: 1px solid #e2e8f0;
      font-size: 15px;
    }
    tr:last-child td {
      border-bottom: none;
    }
    tr:nth-child(even) td { 
      background-color: #f8fafc;
    }
    tr:hover td {
      background-color: #e0f2fe;
    }
    .value-cell {
      font-weight: 600;
      color: #1e3a8a;
    }
    .recommendations {
      background: linear-gradient(135deg, #eff6ff 0%, #ecfdf5 100%);
      border-left: 4px solid #10b981;
      padding: 20px;
      border-radius: 8px;
      margin: 20px 0;
    }
    .recommendations ul {
      margin: 10px 0;
      padding-left: 20px;
    }
    .recommendations li {
      margin: 10px 0;
      font-size: 15px;
      color: #334155;
    }
    .footer { 
      margin-top: 50px; 
      text-align: center; 
      padding-top: 20px;
      border-top: 2px solid #e2e8f0;
    }
    .footer p {
      margin: 5px 0;
      color: #64748b;
      font-size: 13px;
    }
    .footer .company {
      font-weight: 600;
      color: #1e3a8a;
      font-size: 15px;
      margin-top: 10px;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>⚡ Smart Cable Configuration Report</h1>
    <p>Edwards 1977 - Exploration Depth Calculator</p>
    <p class="date">Generated: ${new Date().toLocaleString()}</p>
  </div>
  
  <div class="section">
    <h2>Configuration Overview</h2>
    <div class="summary-grid">
      <div class="summary-card">
        <div class="label">Array Type</div>
        <div class="value" style="font-size: 16px;">${systemType}</div>
      </div>
      <div class="summary-card">
        <div class="label">Electrodes</div>
        <div class="value">${numElectrodes}</div>
      </div>
      <div class="summary-card">
        <div class="label">Scaling Factor</div>
        <div class="value">${scalingFactor}%</div>
      </div>
    </div>
  </div>
  
  <div class="section">
    <h2>Metric Measurements</h2>
    <table>
      <tr>
        <th>Measurement</th>
        <th>Value</th>
      </tr>
      <tr>
        <td>Maximum Depth</td>
        <td class="value-cell">${calculations.metric.depth.toFixed(2)} m</td>
      </tr>
      <tr>
        <td>Electrode Spacing</td>
        <td class="value-cell">${calculations.metric.spacing.toFixed(2)} m</td>
      </tr>
      <tr>
        <td>Total Cable Length</td>
        <td class="value-cell">${calculations.metric.totalLength.toFixed(2)} m</td>
      </tr>
      <tr>
        <td>Resolution (Approx.)</td>
        <td class="value-cell">${calculations.metric.resolution.toFixed(2)} m</td>
      </tr>
    </table>
  </div>
  
  <div class="section">
    <h2>Imperial Measurements</h2>
    <table>
      <tr>
        <th>Measurement</th>
        <th>Value</th>
      </tr>
      <tr>
        <td>Maximum Depth</td>
        <td class="value-cell">${calculations.imperial.depth.toFixed(2)} ft</td>
      </tr>
      <tr>
        <td>Electrode Spacing</td>
        <td class="value-cell">${calculations.imperial.spacing.toFixed(2)} ft</td>
      </tr>
      <tr>
        <td>Total Cable Length</td>
        <td class="value-cell">${calculations.imperial.totalLength.toFixed(2)} ft</td>
      </tr>
      <tr>
        <td>Resolution (Approx.)</td>
        <td class="value-cell">${calculations.imperial.resolution.toFixed(2)} ft</td>
      </tr>
    </table>
  </div>
  
  <div class="section">
    <h2>Configuration Notes</h2>
    <div class="recommendations">
      <ul>
        <li><strong>Electrode Connector Spacing:</strong> ${unit === 'metric' ? calculations.metric.spacing.toFixed(2) + ' m' : calculations.imperial.spacing.toFixed(2) + ' ft'}</li>
        <li><strong>Survey Optimization:</strong> Configuration optimized for ${systemType} array type</li>
        <li><strong>Application:</strong> Suitable for geophysical resistivity surveys based on Edwards 1977 methodology</li>
      </ul>
    </div>
  </div>
  
  <div class="footer">
    <p>Smart Cable Configurator</p>
    <p>Based on Edwards 1977 Research</p>
    <p class="company">Advanced Geosciences Inc.</p>
  </div>
</body>
</html>`;
      
      const blob = new Blob([pdfContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cable-config-${Date.now()}.html`;
      a.click();
      toast({ title: 'Success', description: 'Configuration exported as PDF (HTML format)' });
    }
  };

  // Generate Quote
  const generateQuote = () => {
    const quoteText = `
SMART CABLE CONFIGURATION QUOTE
================================
Date: ${new Date().toLocaleDateString()}

SYSTEM CONFIGURATION
--------------------
Array Type: ${systemType}
Number of Electrodes: ${numElectrodes}
Scaling Factor: ${scalingFactor}%

METRIC MEASUREMENTS
-------------------
Spacing: ${calculations.metric.spacing.toFixed(2)} m
Total Cable Length: ${calculations.metric.totalLength.toFixed(2)} m
Maximum Depth: ${calculations.metric.depth.toFixed(2)} m
Resolution (Approx.): ${calculations.metric.resolution.toFixed(2)} m

IMPERIAL MEASUREMENTS
---------------------
Spacing: ${calculations.imperial.spacing.toFixed(2)} ft
Total Cable Length: ${calculations.imperial.totalLength.toFixed(2)} ft
Maximum Depth: ${calculations.imperial.depth.toFixed(2)} ft
Resolution (Approx.): ${calculations.imperial.resolution.toFixed(2)} ft

RECOMMENDATIONS
---------------
• Electrode connector spacing: ${unit === 'metric' ? calculations.metric.spacing.toFixed(2) + ' m' : calculations.imperial.spacing.toFixed(2) + ' ft'}
• Survey type optimized for ${systemType} configuration

--------------------
Advanced Geosciences Inc.
    `;

    const blob = new Blob([quoteText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quote-${Date.now()}.txt`;
    a.click();
    toast({ title: 'Quote Generated', description: 'Your quote has been downloaded' });
  };

  return (
    <div className="space-y-6">
      {/* Generate Quote Button */}
      <div className="flex justify-end">
        <Button
          onClick={generateQuote}
          className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#10b981] text-white transition-all duration-300"
        >
          <FileText className="w-4 h-4 mr-2" />
          Generate Quote
        </Button>
      </div>
        {/* Input Configuration Card */}
        <Card className="mb-8 border-slate-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-emerald-50 border-b">
            <CardTitle className="flex items-center gap-2 text-slate-900">
              <Settings className="w-5 h-5 text-[#1e3a8a]" />
              Configuration Inputs
            </CardTitle>
            <CardDescription>
              <div className="space-y-2">
                <p className="text-sm">Adjust parameters to calculate cable configuration</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setCalcMode('depth')}
                    className={`text-xs px-3 py-1 rounded transition-all ${
                      calcMode === 'depth'
                        ? 'bg-[#1e3a8a] text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    Depth → Spacing
                  </button>
                  <button
                    onClick={() => setCalcMode('spacing')}
                    className={`text-xs px-3 py-1 rounded transition-all ${
                      calcMode === 'spacing'
                        ? 'bg-[#1e3a8a] text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    Spacing → Depth
                  </button>
                  <button
                    onClick={() => setCalcMode('electrodes')}
                    className={`text-xs px-3 py-1 rounded transition-all ${
                      calcMode === 'electrodes'
                        ? 'bg-[#1e3a8a] text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    Depth + Spacing → Electrodes
                  </button>
                </div>
                <p className="text-xs text-slate-600 italic mt-2">
                  💡 Note: Depth factors from Edwards 1977 — select array type to match your survey
                </p>
              </div>
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {/* Array Type (System Type) */}
              <div className="space-y-2">
                <Label htmlFor="systemType" className="text-slate-700 font-medium">Array Type</Label>
                <Select value={systemType} onValueChange={setSystemType}>
                  <SelectTrigger id="systemType" className="border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(SYSTEM_TYPES).map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Input fields change based on calculation mode */}
              {calcMode === 'depth' ? (
                <>
                  {/* Mode 1: Input Depth */}
                  <div className="space-y-2">
                    <Label htmlFor="depthMax" className="text-slate-700 font-medium">
                      Depth Max ({unit === 'metric' ? 'm' : 'ft'})
                    </Label>
                    <Input
                      id="depthMax"
                      type="number"
                      step="0.01"
                      value={depthMax}
                      onChange={(e) => setDepthMax(Number(e.target.value))}
                      className="border-slate-300 bg-red-50"
                      min="0.01"
                    />
                  </div>
                  {/* Number of Electrodes */}
                  <div className="space-y-2">
                    <Label htmlFor="electrodes" className="text-slate-700 font-medium">Number of Electrodes</Label>
                    <Input
                      id="electrodes"
                      type="number"
                      value={calculations.metric.electrodes}
                      onChange={(e) => setNumElectrodes(Number(e.target.value))}
                      className="border-slate-300 bg-red-50"
                      min="2"
                    />
                    <div className="flex gap-1 flex-wrap">
                      {[28, 56, 84, 112].map(count => (
                        <button
                          key={count}
                          onClick={() => setNumElectrodes(count)}
                          className="text-xs px-2 py-0.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                        >
                          {count}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : calcMode === 'spacing' ? (
                <>
                  {/* Mode 2: Input Spacing */}
                  <div className="space-y-2">
                    <Label htmlFor="spacing" className="text-slate-700 font-medium">
                      Spacing ({unit === 'metric' ? 'm' : 'ft'})
                    </Label>
                    <Input
                      id="spacing"
                      type="number"
                      step="0.01"
                      value={spacing}
                      onChange={(e) => setSpacing(Number(e.target.value))}
                      className="border-slate-300 bg-red-50"
                      min="0.01"
                    />
                  </div>
                  {/* Number of Electrodes */}
                  <div className="space-y-2">
                    <Label htmlFor="electrodes" className="text-slate-700 font-medium">Number of Electrodes</Label>
                    <Input
                      id="electrodes"
                      type="number"
                      value={calculations.metric.electrodes}
                      onChange={(e) => setNumElectrodes(Number(e.target.value))}
                      className="border-slate-300 bg-red-50"
                      min="2"
                    />
                    <div className="flex gap-1 flex-wrap">
                      {[28, 56, 84, 112].map(count => (
                        <button
                          key={count}
                          onClick={() => setNumElectrodes(count)}
                          className="text-xs px-2 py-0.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                        >
                          {count}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Mode 3: Input Depth and Spacing */}
                  <div className="space-y-2">
                    <Label htmlFor="depthMax" className="text-slate-700 font-medium">
                      Depth Max ({unit === 'metric' ? 'm' : 'ft'})
                    </Label>
                    <Input
                      id="depthMax"
                      type="number"
                      step="0.01"
                      value={depthMax}
                      onChange={(e) => setDepthMax(Number(e.target.value))}
                      className="border-slate-300 bg-red-50"
                      min="0.01"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="spacing" className="text-slate-700 font-medium">
                      Spacing ({unit === 'metric' ? 'm' : 'ft'})
                    </Label>
                    <Input
                      id="spacing"
                      type="number"
                      step="0.01"
                      value={spacing}
                      onChange={(e) => setSpacing(Number(e.target.value))}
                      className="border-slate-300 bg-red-50"
                      min="0.01"
                    />
                  </div>
                  {/* Show calculated electrodes with recommendations */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Calculated Electrodes</Label>
                    <div className="p-3 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-lg border-2 border-[#1e3a8a]">
                      <p className="text-3xl font-bold text-[#1e3a8a]">{calculations.metric.electrodes}</p>
                    </div>
                    <p className="text-xs text-slate-600">Recommended standard configurations:</p>
                    <div className="flex gap-1 flex-wrap">
                      {[28, 56, 84, 112].map(count => {
                        const isRecommended = count >= calculations.metric.electrodes && 
                                             (count === 28 || 
                                              (count === 56 && calculations.metric.electrodes > 28) ||
                                              (count === 84 && calculations.metric.electrodes > 56) ||
                                              (count === 112 && calculations.metric.electrodes > 84));
                        const isNext = [28, 56, 84, 112].filter(c => c >= calculations.metric.electrodes)[0] === count;
                        
                        return (
                          <button
                            key={count}
                            className={`text-xs px-2 py-0.5 rounded transition-colors ${
                              isNext
                                ? 'bg-gradient-to-r from-[#1e3a8a] to-[#10b981] text-white font-bold ring-2 ring-[#10b981] ring-offset-2'
                                : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
                            }`}
                          >
                            {count}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* Scaling Exploration Depth - Read Only */}
              <div className="space-y-2">
                <Label htmlFor="scalingFactor" className="text-slate-700 font-medium">
                  Scaling Exploration Depth (%)
                </Label>
                <Input
                  id="scalingFactor"
                  type="number"
                  value={scalingFactor}
                  readOnly
                  disabled
                  className="border-slate-300 bg-slate-100 cursor-not-allowed"
                />
                <p className="text-xs text-slate-500">Auto-set by Array Type</p>
              </div>

              {/* Unit Toggle */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Unit System</Label>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleUnitChange('metric')}
                    variant={unit === 'metric' ? 'default' : 'outline'}
                    className={unit === 'metric' ? 'bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white flex-1' : 'flex-1'}
                  >
                    Metric
                  </Button>
                  <Button
                    onClick={() => handleUnitChange('imperial')}
                    variant={unit === 'imperial' ? 'default' : 'outline'}
                    className={unit === 'imperial' ? 'bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white flex-1' : 'flex-1'}
                  >
                    Imperial
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Display */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
            <TabsTrigger value="metric">Metric Results</TabsTrigger>
            <TabsTrigger value="imperial">Imperial Results</TabsTrigger>
          </TabsList>

          {/* Metric Tab */}
          <TabsContent value="metric">
            <Card className="border-slate-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-emerald-50 border-b">
                <CardTitle className="text-slate-900">Metric Measurements</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <ResultCard
                    label="Depth Max"
                    value={calculations.metric.depth.toFixed(2)}
                    unit="m"
                    icon={TrendingUp}
                    isInput={calcMode === 'depth' || calcMode === 'electrodes'}
                  />
                  <ResultCard
                    label="Electrodes"
                    value={calculations.metric.electrodes}
                    unit=""
                    icon={Zap}
                    isInput={calcMode === 'depth' || calcMode === 'spacing'}
                  />
                  <ResultCard
                    label="Spacing"
                    value={calculations.metric.spacing.toFixed(2)}
                    unit="m"
                    icon={Calculator}
                    isInput={calcMode === 'spacing' || calcMode === 'electrodes'}
                  />
                  <ResultCard
                    label="Total Length"
                    value={calculations.metric.totalLength.toFixed(2)}
                    unit="m"
                    icon={Calculator}
                    isInput={false}
                  />
                  <ResultCard
                    label="Scaling Depth"
                    value={scalingFactor.toFixed(2)}
                    unit="%"
                    icon={Calculator}
                    isInput={false}
                  />
                  <ResultCard
                    label="Resolution"
                    value={calculations.metric.resolution.toFixed(2)}
                    unit="m"
                    icon={Calculator}
                    isInput={false}
                  />
                </div>

                {/* Electrode Array Visualization */}
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Electrode Array Visualization</h3>
                  <div className="bg-white p-6 rounded-lg border-2 border-slate-300">
                    <ElectrodeArrayPlot
                      numElectrodes={calculations.metric.electrodes}
                      spacing={calculations.metric.spacing}
                      depth={calculations.metric.depth}
                      scalingFactor={scalingFactor}
                      arrayType={systemType}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Imperial Tab */}
          <TabsContent value="imperial">
            <Card className="border-slate-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-emerald-50 border-b">
                <CardTitle className="text-slate-900">Imperial Measurements</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <ResultCard
                    label="Depth Max"
                    value={calculations.imperial.depth.toFixed(2)}
                    unit="ft"
                    icon={TrendingUp}
                    isInput={calcMode === 'depth' || calcMode === 'electrodes'}
                  />
                  <ResultCard
                    label="Electrodes"
                    value={calculations.imperial.electrodes}
                    unit=""
                    icon={Zap}
                    isInput={calcMode === 'depth' || calcMode === 'spacing'}
                  />
                  <ResultCard
                    label="Spacing"
                    value={calculations.imperial.spacing.toFixed(2)}
                    unit="ft"
                    icon={Calculator}
                    isInput={calcMode === 'spacing' || calcMode === 'electrodes'}
                  />
                  <ResultCard
                    label="Total Length"
                    value={calculations.imperial.totalLength.toFixed(2)}
                    unit="ft"
                    icon={Calculator}
                    isInput={false}
                  />
                  <ResultCard
                    label="Scaling Depth"
                    value={scalingFactor.toFixed(2)}
                    unit="%"
                    icon={Calculator}
                    isInput={false}
                  />
                  <ResultCard
                    label="Resolution"
                    value={calculations.imperial.resolution.toFixed(2)}
                    unit="ft"
                    icon={Calculator}
                    isInput={false}
                  />
                </div>

                {/* Electrode Array Visualization */}
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Electrode Array Visualization</h3>
                  <div className="bg-white p-6 rounded-lg border-2 border-slate-300">
                    <ElectrodeArrayPlot
                      numElectrodes={calculations.imperial.electrodes}
                      spacing={calculations.imperial.spacing}
                      depth={calculations.imperial.depth}
                      scalingFactor={scalingFactor}
                      arrayType={systemType}
                      unit="ft"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Unit Converter & Export */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Unit Converter */}
          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-emerald-50 border-b">
              <CardTitle className="text-slate-900">Unit Converter</CardTitle>
              <CardDescription>Quick metric/imperial conversions</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label>Enter Value</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={converterInput}
                  onChange={(e) => setConverterInput(e.target.value)}
                  placeholder="Enter a number"
                  className="border-slate-300"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => convertMeterToFeet(Number(converterInput))}
                  className="flex-1 bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#10b981] text-white"
                >
                  m → ft
                </Button>
                <Button
                  onClick={() => convertFeetToMeter(Number(converterInput))}
                  className="flex-1 bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#10b981] text-white"
                >
                  ft → m
                </Button>
              </div>
              {converterResult && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-slate-600">Result:</p>
                  <p className="text-2xl font-bold text-slate-900">{converterResult}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Export Options */}
          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-emerald-50 border-b">
              <CardTitle className="text-slate-900">Export Configuration</CardTitle>
              <CardDescription>Save your configuration for later use</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <Button
                onClick={() => exportConfig('pdf')}
                className="w-full bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#10b981] text-white justify-start"
              >
                <Download className="w-4 h-4 mr-2" />
                Export as PDF
              </Button>
              <Button
                onClick={() => exportConfig('json')}
                className="w-full bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#10b981] text-white justify-start"
              >
                <Download className="w-4 h-4 mr-2" />
                Export as JSON
              </Button>
              <Button
                onClick={() => exportConfig('csv')}
                className="w-full bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#10b981] text-white justify-start"
              >
                <Download className="w-4 h-4 mr-2" />
                Export as CSV
              </Button>
              <div className="pt-2 text-sm text-slate-600 space-y-1">
                <p>• PDF: Professional report format</p>
                <p>• JSON: Full configuration data</p>
                <p>• CSV: Tabular format for spreadsheets</p>
              </div>
            </CardContent>
          </Card>
        </div>
    </div>
  );
};

// Result Card Component
const ResultCard = ({ label, value, unit, icon: Icon, isInput = false }) => {
  return (
    <div className="group">
      <div className={`p-4 rounded-lg transition-all duration-300 shadow-md ${
        isInput 
          ? 'bg-gradient-to-br from-red-50 to-orange-50 border-red-300 group-hover:from-red-100 group-hover:to-orange-100 group-hover:border-red-400 group-hover:shadow-lg' 
          : 'bg-gradient-to-br from-blue-100 to-emerald-50 border-slate-400 group-hover:from-[#1e3a8a] group-hover:to-[#10b981] group-hover:border-[#10b981] group-hover:shadow-xl'
      }`} style={{ borderWidth: isInput ? '1px' : '2.5px' }}>
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isInput ? 'bg-red-200/50' : 'bg-white/50'
          }`}>
            <Icon className={`w-4 h-4 transition-colors duration-300 ${
              isInput 
                ? 'text-red-700 group-hover:text-red-800' 
                : 'text-[#1e3a8a] group-hover:text-white'
            }`} />
          </div>
        </div>
        <p className={`text-xs mb-1 transition-colors duration-300 ${
          isInput 
            ? 'text-red-700 group-hover:text-red-800 font-medium' 
            : 'text-slate-600 group-hover:text-white/80'
        }`}>
          {label} {isInput && '(Input)'}
        </p>
        <p className={`text-xl font-bold transition-colors duration-300 ${
          isInput 
            ? 'text-red-900 group-hover:text-red-950' 
            : 'text-slate-900 group-hover:text-white'
        }`}>
          {value} <span className="text-sm">{unit}</span>
        </p>
      </div>
    </div>
  );
};

// Electrode Array Visualization Component
const ElectrodeArrayPlot = ({ numElectrodes, spacing, depth, scalingFactor = 17.3, arrayType = 'Wenner', unit = 'm' }) => {
  const horizontalPadding = 80;
  const topLabelSpace = 30;
  const electrodeNumberSpace = 22;
  const bottomAxisSpace = 40;

  // Guard against invalid inputs that cause infinite loops
  const safeElectrodes = Math.max(2, Math.min(Math.round(numElectrodes) || 2, 500));
  const safeSpacing = Math.max(0.01, spacing || 1);
  const safeDepth = Math.max(0.01, depth || 1);

  // Depth factor from 2D-command-creator (source of truth)
  const depthFactor = scalingFactor / 100;

  // Array length
  const arrayLength = (safeElectrodes - 1) * safeSpacing;

  // Calculate max investigation depth based on array type
  const isDipoleBased = arrayType === 'Dipole-Dipole' || arrayType === 'Pole-Dipole';
  let maxPossibleDepth;
  if (isDipoleBased) {
    const maxN = Math.max(1, safeElectrodes - 3);
    maxPossibleDepth = maxN * safeSpacing * depthFactor;
  } else {
    maxPossibleDepth = arrayLength * depthFactor;
  }
  const effectiveDepth = Math.max(0.01, Math.min(safeDepth, maxPossibleDepth));

  // Calculate pixel scaling
  const pixelsPerMeter = Math.min(50, 800 / Math.max(1, arrayLength));
  const surfaceY = topLabelSpace + electrodeNumberSpace + 8;
  const plotBottom = surfaceY + effectiveDepth * pixelsPerMeter;
  const svgWidth = arrayLength * pixelsPerMeter + 2 * horizontalPadding;
  const svgHeight = plotBottom + bottomAxisSpace;

  // Dynamic electrode radius
  const electrodeRadius = Math.max(2, Math.min(5, 140 / safeElectrodes));

  // Generate electrode positions at surface
  const electrodes = Array.from({ length: safeElectrodes }, (_, i) => ({
    index: i + 1,
    x: horizontalPadding + i * safeSpacing * pixelsPerMeter,
    position: i * safeSpacing
  }));

  // Smart electrode labeling: always show 1 and N, then round multiples (5,10,15... or 10,20,30...)
  const plotWidth = svgWidth - 2 * horizontalPadding;
  const pixelPerElectrode = plotWidth / (safeElectrodes - 1);
  const minLabelWidth = 30;
  const rawStep = Math.max(1, Math.ceil(minLabelWidth / Math.max(1, pixelPerElectrode)));
  const niceSteps = [1, 2, 5, 10, 15, 20, 25, 50];
  const electrodeStep = niceSteps.find(s => s >= rawStep) || rawStep;
  const labeledElectrodes = new Set();
  labeledElectrodes.add(1);
  labeledElectrodes.add(safeElectrodes);
  // Label round multiples: 5, 10, 15... or 10, 20, 30... skipping if too close to 1 or N
  for (let n = electrodeStep; n < safeElectrodes; n += electrodeStep) {
    if (n > 1 && Math.abs(n - safeElectrodes) >= electrodeStep * 0.6) {
      labeledElectrodes.add(n);
    }
  }

  // Scatter plot logic: more electrodes = larger step between points (visible gaps)
  const isHighDensity = safeElectrodes > 56;
  const separationStep = isHighDensity ? Math.max(2, Math.floor(safeElectrodes / 40)) : 1;
  const pointStep = isHighDensity ? Math.max(2, Math.floor(safeElectrodes / 40)) : 1;
  const dotRadius = isHighDensity
    ? Math.max(2, Math.min(4, 120 / safeElectrodes))
    : Math.max(3, Math.min(5, 120 / safeElectrodes));

  // Generate pseudo-section points using correct depth formulas per array type
  // Cap at 5000 points to prevent memory issues
  const MAX_POINTS = 5000;
  const pseudoSectionPoints = [];

  if (isDipoleBased) {
    // Dipole-Dipole / Pole-Dipole: z = n × a × depthFactor
    const maxN = Math.max(1, safeElectrodes - 3);
    for (let n = 1; n <= maxN && pseudoSectionPoints.length < MAX_POINTS; n += separationStep) {
      const investigationDepth = n * safeSpacing * depthFactor;
      if (investigationDepth > effectiveDepth) break;

      const y = surfaceY + investigationDepth * pixelsPerMeter;
      const depthFraction = investigationDepth / effectiveDepth;

      for (let i = 0; i < safeElectrodes - n - 1 && pseudoSectionPoints.length < MAX_POINTS; i += pointStep) {
        const midpointX = horizontalPadding + (i + (n + 1) / 2) * safeSpacing * pixelsPerMeter;
        pseudoSectionPoints.push({ x: midpointX, y, depthFraction });
      }
    }
  } else {
    // Wenner, Schlumberger, W-S, Gradient, etc.: z = AB × depthFactor
    const maxAB = safeElectrodes - 1;
    for (let ab = 2; ab <= maxAB && pseudoSectionPoints.length < MAX_POINTS; ab += separationStep) {
      const AB_length = ab * safeSpacing;
      const investigationDepth = AB_length * depthFactor;
      if (investigationDepth > effectiveDepth) break;

      const y = surfaceY + investigationDepth * pixelsPerMeter;
      const depthFraction = investigationDepth / effectiveDepth;

      const numPositions = safeElectrodes - ab;
      for (let i = 0; i < numPositions && pseudoSectionPoints.length < MAX_POINTS; i += pointStep) {
        const midpointX = horizontalPadding + (i + ab / 2) * safeSpacing * pixelsPerMeter;
        pseudoSectionPoints.push({ x: midpointX, y, depthFraction });
      }
    }
  }

  // Color scale
  const getDepthColor = (f) => {
    if (f < 0.2) return '#3B82F6';
    if (f < 0.4) return '#06B6D4';
    if (f < 0.6) return '#10B981';
    if (f < 0.8) return '#F59E0B';
    return '#EF4444';
  };

  // Depth grid
  const depthLevels = Array.from({ length: 5 }, (_, i) => (effectiveDepth * i) / 4);

  // Distance axis ticks (bottom)
  const distanceTicks = [];
  const niceInterval = (() => {
    const raw = arrayLength / 6;
    const mag = Math.pow(10, Math.floor(Math.log10(raw)));
    const choices = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000];
    for (const c of choices) { if (c * mag >= raw) return c * mag; }
    return raw;
  })();
  for (let d = 0; d <= arrayLength; d += niceInterval) {
    distanceTicks.push(d);
  }
  // Always include the last tick if not already close
  if (arrayLength - distanceTicks[distanceTicks.length - 1] > niceInterval * 0.3) {
    distanceTicks.push(arrayLength);
  }

  return (
    <div className="overflow-x-auto">
      <svg width={svgWidth} height={svgHeight} className="mx-auto">
        {/* Electrode Number label */}
        <text
          x={svgWidth / 2}
          y={topLabelSpace - 6}
          textAnchor="middle"
          className="text-sm font-semibold fill-slate-700"
        >
          Electrode Number
        </text>

        {/* Electrode numbers */}
        {electrodes.filter(e => labeledElectrodes.has(e.index)).map(electrode => (
          <text
            key={`enum-${electrode.index}`}
            x={electrode.x}
            y={topLabelSpace + 12}
            textAnchor="middle"
            className="text-xs font-medium fill-slate-700"
          >
            {electrode.index}
          </text>
        ))}

        {/* Surface line */}
        <line
          x1={horizontalPadding}
          y1={surfaceY}
          x2={svgWidth - horizontalPadding}
          y2={surfaceY}
          stroke="#1e3a8a"
          strokeWidth="3"
        />

        {/* Y-axis */}
        <line
          x1={horizontalPadding}
          y1={surfaceY}
          x2={horizontalPadding}
          y2={plotBottom}
          stroke="#475569"
          strokeWidth="2"
        />

        {/* Y-axis label */}
        <text
          x={20}
          y={(surfaceY + plotBottom) / 2}
          textAnchor="middle"
          transform={`rotate(-90, 20, ${(surfaceY + plotBottom) / 2})`}
          className="text-sm font-semibold fill-slate-700"
        >
          Depth ({unit})
        </text>

        {/* Depth grid lines */}
        {depthLevels.map((d, idx) => {
          const y = surfaceY + d * pixelsPerMeter;
          return (
            <g key={`dg-${idx}`}>
              <line
                x1={horizontalPadding}
                y1={y}
                x2={svgWidth - horizontalPadding}
                y2={y}
                stroke="#CBD5E1"
                strokeWidth="1"
                strokeDasharray="4,4"
              />
              <text
                x={horizontalPadding - 10}
                y={y + 4}
                textAnchor="end"
                className="text-xs fill-slate-600"
              >
                {d.toFixed(0)}
              </text>
            </g>
          );
        })}

        {/* Electrodes at surface */}
        {electrodes.map(electrode => (
          <g key={`el-${electrode.index}`}>
            <line
              x1={electrode.x}
              y1={surfaceY}
              x2={electrode.x}
              y2={surfaceY + 8}
              stroke="#1e3a8a"
              strokeWidth="1.5"
            />
            <circle
              cx={electrode.x}
              cy={surfaceY}
              r={electrodeRadius}
              fill="#1e3a8a"
              stroke="#1e40af"
              strokeWidth="1.5"
            />
          </g>
        ))}

        {/* Pseudo-section scatter points */}
        {pseudoSectionPoints.map((pt, idx) => (
          <circle
            key={`dp-${idx}`}
            cx={pt.x}
            cy={pt.y}
            r={dotRadius}
            fill={getDepthColor(pt.depthFraction)}
            fillOpacity={0.75}
            stroke="none"
          />
        ))}

        {/* Bottom X-axis line */}
        <line
          x1={horizontalPadding}
          y1={plotBottom}
          x2={svgWidth - horizontalPadding}
          y2={plotBottom}
          stroke="#475569"
          strokeWidth="2"
        />

        {/* Distance ticks at bottom */}
        {distanceTicks.map((d, idx) => {
          const x = horizontalPadding + d * pixelsPerMeter;
          return (
            <g key={`dt-${idx}`}>
              <line x1={x} y1={plotBottom} x2={x} y2={plotBottom + 6} stroke="#475569" strokeWidth="1.5" />
              <text x={x} y={plotBottom + 18} textAnchor="middle" className="text-xs fill-slate-600">
                {d.toFixed(0)}
              </text>
            </g>
          );
        })}

        {/* Distance axis label */}
        <text
          x={svgWidth / 2}
          y={plotBottom + 34}
          textAnchor="middle"
          className="text-sm font-semibold fill-slate-700"
        >
          Distance ({unit})
        </text>
      </svg>

      {/* Summary info */}
      <div className="mt-4 text-center">
        <div className="text-sm text-slate-700">
          <strong>Configuration:</strong> {safeElectrodes} electrodes |
          <strong> Spacing:</strong> {safeSpacing.toFixed(2)} {unit} |
          <strong> Array Length:</strong> {arrayLength.toFixed(2)} {unit}
        </div>
        <div className="text-sm text-slate-600 mt-1">
          <strong>Maximum Investigation Depth:</strong> {effectiveDepth.toFixed(2)} {unit}
        </div>
      </div>
    </div>
  );
};

export default CableConfigurator;